/**
 * Database Capabilities Utility
 *
 * Defines which database types support various SQL and database operations.
 * This provides a single source of truth for feature support across the application.
 */

/**
 * Database types that support SQL-style functions (stored functions, UDFs)
 * Key-value stores (Redis, Memcached) and document stores (MongoDB) don't support these
 * ClickHouse supports user-defined functions (UDFs)
 * ScyllaDB supports user-defined functions (UDFs) and user-defined aggregates (UDAs)
 */
const SQL_FUNCTION_SUPPORTED_TYPES = new Set([
  'mysql',
  'postgresql',
  'oracle',
  'sqlserver',
  'mariadb',
  'clickhouse',
  'scylladb'
])

/**
 * Database types that support stored procedures
 */
const PROCEDURE_SUPPORTED_TYPES = new Set([
  'mysql',
  'postgresql',
  'oracle',
  'sqlserver',
  'mariadb'
])

/**
 * Database types that support schemas (namespaces within databases)
 */
const SCHEMA_SUPPORTED_TYPES = new Set([
  'postgresql',
  'oracle',
  'sqlserver'
])

/**
 * Database types that are key-value stores (no SQL support)
 */
const KEY_VALUE_STORE_TYPES = new Set([
  'redis',
  'redis-cluster',
  'memcached'
])

/**
 * Database types that are document stores (document-oriented, not SQL)
 */
const DOCUMENT_STORE_TYPES = new Set([
  'mongodb'
])

/**
 * ClickHouse OLAP database type
 */
const CLICKHOUSE_TYPE = new Set([
  'clickhouse'
])

/**
 * ScyllaDB NoSQL database type
 */
const SCYLLADB_TYPE = new Set([
  'scylladb'
])

/**
 * Milvus vector database type
 */
const MILVUS_TYPE = new Set([
  'milvus'
])

/**
 * Database types that support User-Defined Types (UDTs)
 */
const UDT_SUPPORTED_TYPES = new Set([
  'scylladb',
  'postgresql'
])

/**
 * Database types that support User-Defined Aggregates (UDAs)
 */
const UDA_SUPPORTED_TYPES = new Set([
  'scylladb'
])

/**
 * Database types that support Change Data Capture (CDC)
 */
const CDC_SUPPORTED_TYPES = new Set([
  'scylladb'
])

/**
 * Database types that support Service Levels (workload prioritization)
 */
const SERVICE_LEVEL_SUPPORTED_TYPES = new Set([
  'scylladb'
])

/**
 * Database types that support partitions
 */
const PARTITION_SUPPORTED_TYPES = new Set([
  'clickhouse',
  'oracle',
  'milvus'
])


/**
 * Database types that support TTL (Time-To-Live)
 */
const TTL_SUPPORTED_TYPES = new Set([
  'clickhouse',
  'scylladb'
])

/**
 * Database types that support dictionaries
 */
const DICTIONARY_SUPPORTED_TYPES = new Set([
  'clickhouse'
])

/**
 * Database types that support projections
 */
const PROJECTION_SUPPORTED_TYPES = new Set([
  'clickhouse'
])

/**
 * Database types that support materialized view refresh
 */
const MV_REFRESH_SUPPORTED_TYPES = new Set([
  'clickhouse'
])

/**
 * Normalize database type string for comparison
 * Handles variations like 'PostgreSQL', 'postgresql', 'POSTGRESQL'
 */
function normalizeDbType(databaseType: string | undefined | null): string {
  if (!databaseType) return ''
  return databaseType.toLowerCase().trim()
}

/**
 * Check if a database type supports SQL functions
 */
export function supportsFunctions(databaseType: string | undefined | null): boolean {
  return SQL_FUNCTION_SUPPORTED_TYPES.has(normalizeDbType(databaseType))
}

/**
 * Check if a database type supports stored procedures
 */
export function supportsProcedures(databaseType: string | undefined | null): boolean {
  return PROCEDURE_SUPPORTED_TYPES.has(normalizeDbType(databaseType))
}

/**
 * Check if a database type supports schemas
 */
function supportsSchemas(databaseType: string | undefined | null): boolean {
  return SCHEMA_SUPPORTED_TYPES.has(normalizeDbType(databaseType))
}

// Keep reference to avoid unused variable lint error
void supportsSchemas

/**
 * Check if a database type is a key-value store
 */
export function isKeyValueStore(databaseType: string | undefined | null): boolean {
  return KEY_VALUE_STORE_TYPES.has(normalizeDbType(databaseType))
}

/**
 * Check if a database type is a document store (like MongoDB)
 */
export function isDocumentStore(databaseType: string | undefined | null): boolean {
  return DOCUMENT_STORE_TYPES.has(normalizeDbType(databaseType))
}

/**
 * Check if a database type is ClickHouse
 */
function isClickHouse(databaseType: string | undefined | null): boolean {
  return CLICKHOUSE_TYPE.has(normalizeDbType(databaseType))
}

// Keep reference to avoid unused variable lint error
void isClickHouse

/**
 * Check if a database type is ScyllaDB
 */
export function isScyllaDB(databaseType: string | undefined | null): boolean {
  return SCYLLADB_TYPE.has(normalizeDbType(databaseType))
}

/**
 * Check if a database type is Milvus vector database
 */
export function isMilvus(databaseType: string | undefined | null): boolean {
  return MILVUS_TYPE.has(normalizeDbType(databaseType))
}


/**
 * Check if a database type is Redis Cluster
 */
function isRedisCluster(databaseType: string | undefined | null): boolean {
  return normalizeDbType(databaseType) === 'redis-cluster' || normalizeDbType(databaseType) === 'rediscluster'
}

// Keep reference to avoid unused variable lint error
void isRedisCluster

/**
 * Check if a database type is Redis (standalone only, not cluster)
 */
function isRedisStandalone(databaseType: string | undefined | null): boolean {
  return normalizeDbType(databaseType) === 'redis'
}

// Keep reference to avoid unused variable lint error
void isRedisStandalone

/**
 * Check if a database type is any Redis type (standalone or cluster)
 */
export function isAnyRedis(databaseType: string | undefined | null): boolean {
  const normalized = normalizeDbType(databaseType)
  return normalized === 'redis' || normalized === 'redis-cluster' || normalized === 'rediscluster'
}

/**
 * Check if a database type supports User-Defined Types (UDTs)
 */
function supportsUDTs(databaseType: string | undefined | null): boolean {
  return UDT_SUPPORTED_TYPES.has(normalizeDbType(databaseType))
}

/**
 * Check if a database type supports User-Defined Aggregates (UDAs)
 */
function supportsUDAs(databaseType: string | undefined | null): boolean {
  return UDA_SUPPORTED_TYPES.has(normalizeDbType(databaseType))
}

/**
 * Check if a database type supports Change Data Capture (CDC)
 */
function supportsCDC(databaseType: string | undefined | null): boolean {
  return CDC_SUPPORTED_TYPES.has(normalizeDbType(databaseType))
}

/**
 * Check if a database type supports Service Levels
 */
function supportsServiceLevels(databaseType: string | undefined | null): boolean {
  return SERVICE_LEVEL_SUPPORTED_TYPES.has(normalizeDbType(databaseType))
}

/**
 * Check if a database type supports partitions
 */
function supportsPartitions(databaseType: string | undefined | null): boolean {
  return PARTITION_SUPPORTED_TYPES.has(normalizeDbType(databaseType))
}

/**
 * Check if a database type supports TTL (Time-To-Live)
 */
function supportsTTL(databaseType: string | undefined | null): boolean {
  return TTL_SUPPORTED_TYPES.has(normalizeDbType(databaseType))
}

/**
 * Check if a database type supports dictionaries
 */
function supportsDictionaries(databaseType: string | undefined | null): boolean {
  return DICTIONARY_SUPPORTED_TYPES.has(normalizeDbType(databaseType))
}

/**
 * Check if a database type supports projections
 */
function supportsProjections(databaseType: string | undefined | null): boolean {
  return PROJECTION_SUPPORTED_TYPES.has(normalizeDbType(databaseType))
}

/**
 * Check if a database type supports materialized view refresh
 */
function supportsMaterializedViewRefresh(databaseType: string | undefined | null): boolean {
  return MV_REFRESH_SUPPORTED_TYPES.has(normalizeDbType(databaseType))
}

/**
 * Check if a database type is MongoDB
 */
export function isMongoDB(databaseType: string | undefined | null): boolean {
  return normalizeDbType(databaseType) === 'mongodb'
}

/**
 * Check if a database type supports SQL queries
 * Key-value stores and document stores use their own command syntax instead
 */
function supportsSqlQueries(databaseType: string | undefined | null): boolean {
  return !isKeyValueStore(databaseType) && !isDocumentStore(databaseType)
}

/**
 * Check if a database type supports collections (document store terminology)
 */
function supportsCollections(databaseType: string | undefined | null): boolean {
  return isDocumentStore(databaseType)
}

/**
 * Check if a database type supports aggregation pipelines
 */
function supportsAggregationPipeline(databaseType: string | undefined | null): boolean {
  return isDocumentStore(databaseType)
}

// Keep references to avoid unused variable lint errors
void supportsUDTs
void supportsUDAs
void supportsCDC
void supportsServiceLevels
void supportsPartitions
void supportsTTL
void supportsDictionaries
void supportsProjections
void supportsMaterializedViewRefresh
void supportsSqlQueries
void supportsCollections
void supportsAggregationPipeline

/**
 * Database display information configuration
 */
interface DatabaseTypeInfo {
  displayName: string
  features: string[]
  colorScheme: 'blue' | 'orange' | 'red' | 'green' | 'purple' | 'cyan' | 'yellow'
}

const DATABASE_TYPE_INFO: Record<string, DatabaseTypeInfo> = {
  postgresql: {
    displayName: 'PostgreSQL',
    features: [
      'Advanced data types and extensions',
      'Schema-based organization',
      'ACID compliance and transactions',
      'Full-text search capabilities',
      'JSON/JSONB support'
    ],
    colorScheme: 'blue'
  },
  mysql: {
    displayName: 'MySQL',
    features: [
      'High performance and scalability',
      'Multiple storage engines',
      'Replication and clustering',
      'Partitioning support',
      'JSON data type support'
    ],
    colorScheme: 'orange'
  },
  redis: {
    displayName: 'Redis',
    features: [
      'In-memory data storage',
      'Key-value and data structure operations',
      'Pub/Sub messaging',
      'Lua scripting support',
      'Clustering and replication'
    ],
    colorScheme: 'red'
  },
  'redis-cluster': {
    displayName: 'Redis Cluster',
    features: [
      'Distributed in-memory data storage',
      'Automatic data sharding across nodes',
      'High availability with master-replica failover',
      'Horizontal scaling capabilities',
      'Cross-slot pipeline operations'
    ],
    colorScheme: 'red'
  },
  memcached: {
    displayName: 'Memcached',
    features: [
      'High-performance distributed caching',
      'Simple key-value storage',
      'Memory-efficient design',
      'Multi-threaded architecture',
      'Automatic data expiration (TTL)'
    ],
    colorScheme: 'green'
  },
  mongodb: {
    displayName: 'MongoDB',
    features: [
      'Document-oriented storage',
      'Flexible schema design',
      'Aggregation framework',
      'Horizontal scaling with sharding',
      'Rich query language'
    ],
    colorScheme: 'green'
  },
  oracle: {
    displayName: 'Oracle',
    features: [
      'Enterprise-grade reliability',
      'Advanced security features',
      'Partitioning and clustering',
      'PL/SQL procedural language',
      'Data warehousing capabilities'
    ],
    colorScheme: 'red'
  },
  sqlserver: {
    displayName: 'SQL Server',
    features: [
      'Enterprise data management',
      'Business intelligence integration',
      'Always On availability',
      'T-SQL procedural language',
      'In-memory OLTP'
    ],
    colorScheme: 'blue'
  },
  mariadb: {
    displayName: 'MariaDB',
    features: [
      'MySQL compatibility',
      'Enhanced performance',
      'Advanced storage engines',
      'Galera cluster support',
      'Open-source community driven'
    ],
    colorScheme: 'cyan'
  },
  clickhouse: {
    displayName: 'ClickHouse',
    features: [
      'Column-oriented OLAP database',
      'MergeTree engine family',
      'Partitioning and TTL support',
      'Materialized views with refresh',
      'High-performance analytics'
    ],
    colorScheme: 'yellow'
  },
  scylladb: {
    displayName: 'ScyllaDB',
    features: [
      'Distributed NoSQL database',
      'Cassandra-compatible CQL',
      'User-Defined Types and Functions',
      'Change Data Capture (CDC)',
      'Service Levels for workload prioritization'
    ],
    colorScheme: 'purple'
  },
  milvus: {
    displayName: 'Milvus',
    features: [
      'Vector similarity search',
      'Multiple index types (HNSW, IVF, FLAT)',
      'Hybrid search with scalar filtering',
      'Partition-based data organization',
      'Collection schema with typed fields'
    ],
    colorScheme: 'blue'
  }
}

/**
 * Get display name for a database type
 */
export function getDatabaseDisplayName(databaseType: string | undefined | null): string {
  const normalized = normalizeDbType(databaseType)
  return DATABASE_TYPE_INFO[normalized]?.displayName || databaseType || 'Unknown'
}

/**
 * Get features list for a database type
 */
export function getDatabaseFeatures(databaseType: string | undefined | null): string[] {
  const normalized = normalizeDbType(databaseType)
  return DATABASE_TYPE_INFO[normalized]?.features || []
}

/**
 * Get color scheme for a database type
 */
export function getDatabaseColorScheme(databaseType: string | undefined | null): string {
  const normalized = normalizeDbType(databaseType)
  return DATABASE_TYPE_INFO[normalized]?.colorScheme || 'gray'
}

/**
 * Connection type for DatabaseConnection interface
 */
export type ConnectionType = 'mysql' | 'postgresql' | 'redis' | 'redis-cluster' | 'memcached' | 'oracle' | 'mongodb' | 'clickhouse' | 'scylladb' | 'milvus'

/**
 * Map from API database type names to connection type
 * API uses PascalCase (e.g., 'PostgreSQL', 'MySQL', 'Redis', 'Memcached', 'OracleDB', 'MongoDB')
 * Connection type uses lowercase (e.g., 'postgresql', 'mysql', 'redis', 'memcached', 'oracle', 'mongodb')
 *
 * Mappings:
 * - Handle API type 'OracleDB' -> 'oracle'
 * - MongoDB document store -> 'mongodb'
 * - ClickHouse OLAP database -> 'clickhouse'
 * - MariaDB is MySQL-compatible -> 'mysql'
 */
const API_TO_CONNECTION_TYPE: Record<string, ConnectionType> = {
  postgresql: 'postgresql',
  mysql: 'mysql',
  redis: 'redis',
  'redis-cluster': 'redis-cluster',
  rediscluster: 'redis-cluster',
  memcached: 'memcached',
  oracle: 'oracle',
  oracledb: 'oracle',
  mongodb: 'mongodb',
  clickhouse: 'clickhouse',
  mariadb: 'mysql',
  scylladb: 'scylladb',
  milvus: 'milvus',
}

/**
 * Convert API database type to connection type
 * Maps API types like 'PostgreSQL', 'MySQL', 'Redis', 'Memcached' to connection types
 * @param apiDatabaseType - The database type from API (e.g., 'PostgreSQL', 'MySQL', 'Redis', 'Memcached')
 * @returns The connection type for DatabaseConnection interface
 */
export function toConnectionType(apiDatabaseType: string | undefined | null): ConnectionType {
  const normalized = normalizeDbType(apiDatabaseType)
  return API_TO_CONNECTION_TYPE[normalized] || 'postgresql'
}

/**
 * Check if a database type is Oracle
 */
function isOracle(databaseType: string | undefined | null): boolean {
  const normalized = normalizeDbType(databaseType)
  return normalized === 'oracle' || normalized === 'oracledb'
}

// Keep reference to avoid unused variable lint error
void isOracle
